/* 
This file contains all local storage keys constant.

The constant for key name should start with LS_KEY_(key_name) and key name should be in camel case.
e.g. loginToken => LS_KEY_LOGIN_TOKEN = 'loginToken'
*/

// chunk-load failure reload guard (sessionStorage; routes/config.tsx)
export const LS_KEY_CHUNK_RELOAD_AT = 'chunk-reload-at';

// admin skill management breadcrumb (sessionStorage; per-tab so each tab keeps
// its own navigation history without clobbering others)
export const LS_KEY_SKILL_HEIRARCHY_BREADCRUMB = 'skillHeirarchyBreadcrumb';

// skill builder settings
export const LS_KEY_IS_SKILL_BUILDER_AUDIO_ENABLED =
  'isSkillBuilderAudioEnabled';

// applicant review page keys
export const LS_KEY_SHOW_UPLOADED_RESUME = 'showUploadedResume';
export const LS_KEY_SHOW_DETAILED_RESUME = 'showDetailedResume';
export const LS_KEY_SHOW_UPLOADED_RESUME_FULL = 'showUploadedResumeFull';
export const LS_KEY_SHOW_ASSESSMENT_FULL = 'showAssessmentFull';
export const LS_KEY_JOB_APPLICANT_REVIEW_FILTERS = 'jobApplicantReviewFilters';

// chat keys
export const LS_KEY_SEND_MESSAGE_SETTING = 'sendMessageSetting';
export const LS_KEY_JOB_CHAT_TEMPLATES = 'jobChatTemplates';

// employer premium keys
export const LS_KEY_PREMIUM_SELECTED_JOB_ID = 'premium_selected_job_id';
export const LS_KEY_PREMIUM_SELECTED_TYPE = 'premium_selected_type';

// auth tokens
export const LS_KEY_LOGIN_TOKEN = 'loginToken';

// A/B experiment anonymous visitor id — a stable UUID generated by `landing` to
// bucket logged-out users into experiment variants. Shared with the web SPA via
// same-origin localStorage; read-only here (the SPA never generates it) so a
// login/signup completed on the SPA still reconciles to the exposure that was
// logged on landing.
export const LS_KEY_ANON_ID = 'anonId';

// first-touch attribution: full landing URL on first visit (always captured, not just when UTMs present);
// sent as `source_url` on signup so it survives multi-day journeys before the user converts
export const LS_KEY_SIGNUP_SOURCE_URL = 'signupSourceUrl';
// HTTP referrer at first visit — used to infer traffic source when no UTM params are present
export const LS_KEY_SIGNUP_REFERRER_URL = 'signupReferrerUrl';
// first-touch referral hash from the `referral_hash` query param (e.g. ?referral_hash=KUSM9688);
// sent as `referred_by_hash` on signup so the referring user (incl. campus ambassadors) gets credited
export const LS_KEY_SIGNUP_REFERRAL_HASH = 'signupReferralHash';
// THIS visit's landing URL and referrer, in sessionStorage rather than
// localStorage. The signup keys above are first-touch and never overwritten,
// which is right for "who acquired this user" and wrong for "what brought them
// back today" — a login needs the current session's entry point.
export const SS_KEY_LOGIN_SOURCE_URL = 'loginSourceUrl';
export const SS_KEY_LOGIN_REFERRER_URL = 'loginReferrerUrl';

// user onboarding key
export const LS_KEY_ON_AUTH_REDIRECT_PATHNAME = 'onAuthRedirectPathname';

// invite applicants
export const LS_KEY_SHOW_DB_RESUME_DOWNLOAD_CONFIRMATION =
  'showDBResumeDownloadConfirmation';
export const LS_KEY_WHITE_LABEL_RESUME = 'whiteLabelResume';
export const LS_KEY_WHITE_LABEL_RESUME_TYPE = 'whiteLabelResumeType';
export const LS_KEY_HIDE_CONTACT_DETAILS_FROM_RESUME =
  'hideContactDetailsFromResume';
export const LS_KEY_DOWNLOAD_ORIGINAL_APPLICANT_RESUME =
  'downloadOriginalApplicantResume';

// job filters
export const LS_KEY_RECENT_SEARCH_FILTER_JOB_LOCATIONS =
  'recentSearchFilterJobLocations';

// competition
export const LS_KEY_SHOW_COMPETITION_TIMELINE_MOBILE =
  'showCompetitionTimelineMobile';

export const LS_KEY_COMPETITION_ANOMALY_DETECTED =
  'competition_anomaly_detected';

export const LS_KEY_IS_COMPETITIONS_AUDIO_ENABLED =
  'isCompetitionsAudioEnabled';

export const LS_KEY_COMPETITION_ATTEMPT_BEHAVIOR =
  'competition_attempt_behavior';

// admin
export const LS_KEY_ADMIN_JOB_REVIEW_FILTERS = 'adminJobReviewFilters';
// Review Jobs: which tab was last open, and the toolbar/filter state kept per
// tab so the review queue and the full listing don't overwrite each other's
// filters.
export const LS_KEY_ADMIN_JOB_REVIEW_TAB = 'adminJobReviewTab';
export const LS_KEY_ADMIN_JOB_REVIEW_TAB_STATE = 'adminJobReviewTabState';
export const jobReviewTabStateKey = (tab: string) =>
  `${LS_KEY_ADMIN_JOB_REVIEW_TAB_STATE}:${tab}`;
export const LS_KEY_SKILL_STATISTICS_STATE = 'skillStatisticsState';
export const LS_KEY_BULK_EMAIL_ADMIN_PASSWORD = 'bulkEmailAdminPassword';
export const LS_KEY_ADMIN_SKILL_DOMAIN_SEARCH = 'adminSkillDomainSearch';
// admin sidebar recency: map of nav link -> last-visited ms epoch, drives the
// "Recent" pinned group at the top of the leftbar (per browser, not synced)
export const LS_KEY_ADMIN_NAV_RECENTS = 'adminNavRecents';
// admin sidebar group collapse state: map of group label -> open boolean, so
// admins keep the groups they don't use collapsed across sessions (per browser)
export const LS_KEY_ADMIN_NAV_GROUPS_OPEN = 'adminNavGroupsOpen';

// free-unlock offer invite link (?free-credit-unlock-offer=true): sticky flag
// meaning "this browser arrived through the offer link", so the activation popup
// still opens after signup / onboarding / an OAuth round-trip strips the query
// string. Set by `helpers/freeUnlockOfferIntent`, and mirrored by the landing
// site (same origin in prod) under the same key. The PROMPTED companion keeps
// the popup to one auto-open per day for each org and offer.
export const LS_KEY_FREE_UNLOCK_OFFER_INTENT = 'freeUnlockOfferIntent';
export const LS_KEY_FREE_UNLOCK_OFFER_PROMPTED = 'freeUnlockOfferPrompted';

// new year offer
export const LS_KEY_NEW_YEAR_OFFER_DISMISSED_AT = 'newYearOfferDismissedAt';

// auto-apply offer popup — JSON array of permanently dismissed offer ids
export const LS_KEY_AUTO_OFFER_DISMISSED_IDS = 'autoOfferDismissedIds';

// dashboard popup banner (admin-managed) — JSON array of permanently dismissed banner ids
export const LS_KEY_POPUP_BANNER_DISMISSED_IDS = 'popupBannerDismissedIds';

// post-apply Pro card cadence — JSON { userId, applies, shownOn: 'YYYY-MM-DD' }
export const LS_KEY_POST_APPLY_PRO_CARD = 'postApplyProCard';

// dashboard WhatsApp number prompt — JSON { userId, dismissCount, snoozedUntil }
export const LS_KEY_WHATSAPP_PROMPT_STATE = 'whatsappPromptState';

// resume builder
export const LS_KEY_IS_PDF = 'isPDF';
export const LS_KEY_RESUME_PREVIEW_MODE = 'resumePreviewMode';
export const LS_KEY_RESUME_VERSION = 'resumeVersion';

// multiplayer
export const LS_KEY_MULTIPLAYER_ATTEMPT_BEHAVIOR =
  'multiplayer_attempt_behavior';

// evaluation assessment
export const LS_KEY_SHOW_ASSESSMENT_CREATED_STEPS =
  'showAssessmentCreatedSteps';

// store
// Stores the path the user came from before entering the store checkout flow,
// so they can be redirected back after a successful purchase.
export const LS_KEY_STORE_POST_PURCHASE_RETURN_PATH =
  'storePostPurchaseReturnPath';

// appearance / theme
export const LS_KEY_THEME_MODE = 'themeMode';

// unused-voucher dashboard surfacing
// per-session guard so the announcement shows once per login, not on every
// in-session dashboard remount (sessionStorage). The permanent "don't show
// again" is stored server-side on user_metadata, not here.
export const LS_KEY_VOUCHER_ANNOUNCEMENT_SHOWN_THIS_SESSION =
  'voucherAnnouncementShownThisSession';

// Campus Ambassador "Message Admins" widget: last-seen markers (ms epoch) that
// drive the unread counts on the chat FAB + the Chat / Announcements tabs.
// Namespaced per user id — a shared device must not bleed one ambassador's
// "seen" state into another's after an account switch.
export const LS_KEY_CA_CHAT_SEEN_AT = 'caChatSeenAt';
export const LS_KEY_CA_ANN_SEEN_AT = 'caAnnouncementSeenAt';
export const caChatSeenKey = (userId: string) =>
  userId ? `${LS_KEY_CA_CHAT_SEEN_AT}:${userId}` : LS_KEY_CA_CHAT_SEEN_AT;
export const caAnnSeenKey = (userId: string) =>
  userId ? `${LS_KEY_CA_ANN_SEEN_AT}:${userId}` : LS_KEY_CA_ANN_SEEN_AT;

// CA missions "one friend, many missions" tip — dismissed once the ambassador
// taps "Got it" so it doesn't nag on every visit.
export const LS_KEY_CA_MISSIONS_TIP_DISMISSED = 'caMissionsTipDismissed';

// CA appointment ("joining") letter: stores the cohort_id whose letter the
// ambassador has downloaded. The dashboard shows the letter prominently during
// the first week (or until download); once this matches the current cohort it
// demotes to a quiet entry point. Keyed by cohort so a new cohort re-prompts.
export const LS_KEY_CA_JOINING_LETTER_DOWNLOADED_AT =
  'caJoiningLetterDownloadedCohort';

// Admin CA cohort-detail applicants table: persists the chosen sort
// (column + direction) so it survives a page refresh instead of resetting.
export const LS_KEY_CA_APPLICANT_SORT = 'caAdminApplicantSort';

// Admin CA cohort-detail: persists the active tab (Applicants / Overview /
// Weeks & Tasks / …) so leaving and returning (or a refresh) keeps your place.
// Suffixed with the admin's user id at the call site so admins sharing a browser
// (or switching accounts) don't clobber each other's selection.
export const LS_KEY_CA_ADMIN_TAB = 'caAdminTab';

// Admin Outreach (Apollo prospecting): persist the Apollo pull filters (titles /
// locations / tech / employee ranges / max results) and the prospect-list view
// filters so an admin returning to /admin/outreach keeps their setup instead of
// re-entering it every visit.
export const LS_KEY_OUTREACH_FILTERS = 'outreachPullFilters';
export const LS_KEY_OUTREACH_LIST_FILTERS = 'outreachListFilters';

// placement-partner onboarding wizard — persists the current step + entered
// form so a mid-flow refresh resumes where the partner left off instead of
// resetting to step 1. Cleared on successful submit.
export const LS_KEY_PP_ONBOARDING = 'placementPartnerOnboarding';

// guided course onboarding (sessionStorage): array of course aliases for which
// the 3-step intro dialog has been dismissed this session
export const LS_KEY_GUIDED_COURSE_ONBOARDING_SEEN =
  'guidedCourseOnboardingSeen';

// AI-rank unlock popup: map of jobId -> true for jobs where the employer ticked
// "don't show automatically again". Until set, the popup auto-opens on every
// visit to a high-volume locked job; the Rank-all button always opens it anyway.
export const LS_KEY_AI_UNLOCK_AUTO_DISMISSED = 'aiUnlockAutoDismissed';

// Admin email-inbox reply draft, keyed per recipient so an unsent reply survives
// navigation / reload (Gmail-style). Cleared once the reply is sent.
export const LS_KEY_EMAIL_REPLY_DRAFT = 'adminEmailReplyDraft';
export const emailReplyDraftKey = (email: string) =>
  `${LS_KEY_EMAIL_REPLY_DRAFT}:${email.toLowerCase()}`;

// Ambassador HQ: one-time relaunch popup for existing ambassadors, and the
// count of reward rungs the ambassador has seen unlocked (to fire the
// celebration overlay only when a NEW rung crosses).
export const LS_KEY_CA_RELAUNCH_SEEN = 'caRelaunchSeen';
export const LS_KEY_CA_SEEN_RUNGS = 'caSeenUnlockedRungs';

// Interview room: the candidate/interviewer's last-picked camera background
// effect (blur or a virtual background preset), carried across sessions.
export const LS_KEY_INTERVIEW_BG_EFFECT = 'interviewBackgroundEffect';

// The Web Push endpoint this browser last registered. Kept so a lapsed grant
// ("Allow until I close this site") can be cleaned off the server on the next
// visit: by then the browser has already dropped the subscription itself.
export const LS_KEY_WEB_PUSH_ENDPOINT = 'webPushEndpoint';

// Browser notifications were switched off in Settings on this browser, keyed
// by user id. The browser's permission stays granted, so without this the
// login sync would quietly subscribe again on the next visit. The bare key is
// the pre-per-account flag, read once to carry it over.
export const LS_KEY_WEB_PUSH_OPTED_OUT = 'webPushOptedOut';
export const webPushOptedOutKey = (userId: string) =>
  `${LS_KEY_WEB_PUSH_OPTED_OUT}:${userId}`;

// Job details: ids of the jobs whose "prepare for this role" popup this
// browser has already shown. Once per job — after that the offer lives on the
// page, under the description.
export const LS_KEY_JOB_COURSE_PROMPT_SEEN = 'jobCoursePromptSeen';

// Course builder: the slide each course's slide show was last on, by course
// id, so Present picks up where the teacher stopped.
export const LS_KEY_COURSE_PRESENT_AT = 'coursePresentAt';

/** A class quiz's written answer, kept on the device until the server has it:
 * `${LS_KEY_QUIZ_ANSWER_DRAFT}:${attemptId}:${questionId}`. */
export const LS_KEY_QUIZ_ANSWER_DRAFT = 'quizAnswerDraft';

// Applicant AI assistant: the first-visit hello bubble has been seen.
export const LS_KEY_AI_ASSISTANT_NUDGE_SEEN = 'aiAssistantNudgeSeen';

// Code editor look (coding practice and quiz coding questions)
export const LS_KEY_CODE_EDITOR_THEME = 'cp-editor-theme';
export const LS_KEY_CODE_EDITOR_FONT = 'cp-editor-font';

// The Abekus Autofill version this browser last ran, and when: what tells an
// extension Chrome paused after an update from one that was never here.
export const LS_KEY_EXTENSION_SEEN = 'abekusExtensionSeen';
// The candidate closed the "Chrome paused Abekus Autofill" notice for good.
export const LS_KEY_EXTENSION_PAUSED_DISMISSED =
  'abekusExtensionPausedDismissed';
